/**
 * useAppSounds.ts
 * 
 * Responsabilidade: Gerenciar a reprodução de sons do sistema usando HTML5 Audio API nativa.
 * Princípios: 
 * - Single Responsibility: Apenas toca sons.
 * - Zero Dependencies: Usa API nativa do browser.
 */

import { useCallback, useRef } from 'react';

// Mapeamento de nomes de som para arquivos
// O usuário deve colocar os arquivos mp3 correspondentes na pasta public/sounds/
const SOUND_MAP = {
    message: '/sounds/message.mp3',
    alert: '/sounds/alert.mp3',
    success: '/sounds/success.mp3'
} as const;

type SoundName = keyof typeof SOUND_MAP;

export const useAppSounds = () => {
    // Cache de objetos Audio para evitar recriar a cada toque
    const audioCache = useRef<Record<string, HTMLAudioElement>>({});

    const playSound = useCallback((name: SoundName) => {
        try {
            const url = SOUND_MAP[name];

            // Reutiliza ou cria nova instância de áudio
            if (!audioCache.current[name]) {
                audioCache.current[name] = new Audio(url);
            }

            const audio = audioCache.current[name];

            // Reseta para o início caso esteja tocando
            audio.currentTime = 0;

            // Tenta tocar (pode falhar se não houver interação do usuário antes)
            const playPromise = audio.play();

            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        // Sucesso silencioso
                    })
                    .catch(error => {
                        // Ignora erros de autoplay (comum se o usuário não interagiu)
                        if (error.name === 'NotAllowedError') {
                            console.debug('[AppSounds] Autoplay bloqueado (esperado até interação do usuário).');
                        } else {
                            console.warn('[AppSounds] Erro ao tocar som:', error);
                        }
                    });
            }
        } catch (error) {
            console.error('Error playing sound:', error);
        }
    }, []);

    return { playSound };
};
